import { useMemo, useRef } from "react";
import {
  Column,
  MuiTable,
  MuiTypography,
  RowProps,
} from "../../../../../../../../components";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import {
  ADD_PROGRAM_FEE_AND_EXPENSE_TYPES,
  AddExpenseOrFeeSummeryColumns,
  AddOrEditExpenseOrFeeFormKey,
} from "../../../../../../modelsAndSchemas/Models";
import { Box } from "@mui/material";
import { addFeesAndExpensesSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { useFormContext } from "react-hook-form";
import {
  calculateTotalTaxAmount,
  getFormattedAmount,
} from "../../../../../../../../helpers/utils";
import { CURRENCY_FORMAT_MAP } from "../../../../../../../../components/textFieldCurrency/CurrencyUtils";

interface AddFeesAndExpenseSummaryProps {
  type: string;
}

export const AddFeesAndExpenseSummary = ({
  type,
}: AddFeesAndExpenseSummaryProps) => {
  const { commonLabels, useFetchLabel } = useSiteCore();
  const getLabel = useFetchLabel(
    addFeesAndExpensesSiteCoreKeys.addFeesAndExpenses,
  );
  const tableRef = useRef<{ resetPagination: () => void }>(null);
  const { getValues } = useFormContext();

  const tableData = useMemo(() => {
    const {
      feeOrExpenseName,
      feeOrExpenseAmountWithFormat,
      billingAmount,
      feeOrExpenseCurrencyName,
      billingCurrencyName,
      billingCurrencyExchangeRate,
      billingAmountWithFormat,
      taxEntries,
    } = getValues();
    const billingCurrencyFormat = CURRENCY_FORMAT_MAP[billingCurrencyName];
    const totalTax = taxEntries?.length
      ? calculateTotalTaxAmount(taxEntries)
      : 0;
    const totalTaxWithFormat = getFormattedAmount(
      totalTax,
      billingCurrencyFormat?.locale,
    );
    const totalInvoiceAmount = Number(billingAmount) + totalTax;
    const totalInvoiceAmountWithFormat = getFormattedAmount(
      totalInvoiceAmount,
      billingCurrencyFormat?.locale,
    );
    return [
      {
        feeOrExpenseName,
        [AddOrEditExpenseOrFeeFormKey.feeOrExpenseAmountWithFormat]: `${feeOrExpenseCurrencyName} ${feeOrExpenseAmountWithFormat}`,
        billingCurrencyExchangeRate,
        [AddOrEditExpenseOrFeeFormKey.billingAmountWithFormat]: `${billingCurrencyName} ${billingAmountWithFormat}`,
        [AddOrEditExpenseOrFeeFormKey.tax]: `${billingCurrencyName} ${totalTaxWithFormat}`,
        totalAmount: `${billingCurrencyName} ${totalInvoiceAmountWithFormat}`,
      },
    ];
  }, []);

  const columns: Column[] = [
    {
      id: AddOrEditExpenseOrFeeFormKey.feeOrExpenseName,
      label: getLabel(
        type === ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_OR_PROJECT_FEE
          ? addFeesAndExpensesSiteCoreKeys.summaryTabFeeName
          : addFeesAndExpensesSiteCoreKeys.expenseName,
      ),
    },
    {
      id: AddOrEditExpenseOrFeeFormKey.feeOrExpenseAmountWithFormat,
      label: getLabel(
        type === ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_OR_PROJECT_FEE
          ? addFeesAndExpensesSiteCoreKeys.summaryTabFeeAmount
          : addFeesAndExpensesSiteCoreKeys.expenseDetailsAmount,
      ),
    },
    {
      id: AddOrEditExpenseOrFeeFormKey.billingCurrencyExchangeRate,
      label: getLabel(addFeesAndExpensesSiteCoreKeys.summaryTabExchangeRate),
    },
    {
      id: AddOrEditExpenseOrFeeFormKey.billingAmountWithFormat,
      label: getLabel(addFeesAndExpensesSiteCoreKeys.summaryTabBillingAmount),
    },
    {
      id: AddOrEditExpenseOrFeeFormKey.tax,
      label: getLabel(addFeesAndExpensesSiteCoreKeys.summaryTabTax),
    },
    {
      id: AddExpenseOrFeeSummeryColumns.totalAmount,
      label: getLabel(
        addFeesAndExpensesSiteCoreKeys.summaryTabTotalInvoiceAmount,
      ),
      className: "text-right",
      render: (row: RowProps) => (
        <Box className="text-right">
          <MuiTypography variant="h3Bold">
            {row?.[AddExpenseOrFeeSummeryColumns.totalAmount]}
          </MuiTypography>
        </Box>
      ),
    },
  ];

  return (
    <Box className="summary-table-container">
      <MuiTable
        ref={tableRef}
        pageId={"addFeesAndExpenseSummary"}
        tableHeaderForm={[]}
        columns={columns}
        tableResults={tableData}
        emptyMessage={commonLabels.noRecordsFound}
        noResultMessage={commonLabels.searchCriteria}
        priorityResults={[]}
        isShowDashIfValueEmpty
        noDataInApi={false}
        isFooterVisible={false}
      />
    </Box>
  );
};
